import { useCallback, useMemo, useRef } from 'react';

import type { ValueType } from './utils';
import type { ExtendedMaterial, Material } from '../../gql';

import { isExtendedMaterialOnTheFly, isMaterialOnTheFly } from '../../utils';

import { isRenderableOption } from './utils';

type SentinelRef = (node: HTMLLIElement | null) => void;

interface UseMaterialScrollSentinelsArgs {
    /** Whether the options are grouped by their material subcategory */
    isGroupedByMaterialSubcategory: boolean;
    /** Whether the user is actively searching */
    isSearchActive: boolean;
    /** The regular material options, pre-sorted by subcategory name */
    materialOptions: Array<ExtendedMaterial | Material>;
    /** The subcategory group names that are currently expanded */
    expandedGroups: Set<string>;
    /** Map from subcategory name to subcategory public id */
    subcategoryIdByName?: Record<string, string>;
    /** Called when a scroll sentinel is intersected (for infinite scrolling) */
    onIntersection?: (subcategoryId?: string) => void;
}

interface MaterialScrollSentinels {
    /**
     * Resolves the IntersectionObserver ref to attach to a rendered option, or null
     * when the option should not carry a scroll sentinel.
     */
    getSentinelRef: (args: {
        option: ValueType;
        options: Array<ValueType>;
    }) => SentinelRef | null;
}

/**
 * Returns the subcategory group name for a regular material option, or null for any
 * option that is not a regular material (placeholders, the add button, or a MOF).
 */
const getMaterialGroupName = (option: ValueType): string | null => {
    if (
        !option ||
        'nonValueOptionId' in option ||
        'emptySubcategoryPlaceholderId' in option ||
        'collapsedSubcategoryPlaceholderId' in option ||
        isMaterialOnTheFly(option) ||
        isExtendedMaterialOnTheFly(option)
    ) {
        return null;
    }
    return option.materialSubcategory.materialSubcategoryName;
};

/**
 * @description Manages the IntersectionObservers used for infinite scrolling and exposes
 * a helper that resolves which sentinel ref (if any) a rendered option should carry.
 * In browse mode each expanded group gets its own sentinel on its last visible option;
 * in search and flat mode a single global sentinel sits on the last visible option.
 * @returns A helper to resolve the sentinel ref for a given option
 */
const useMaterialScrollSentinels = ({
    isGroupedByMaterialSubcategory,
    isSearchActive,
    materialOptions,
    expandedGroups,
    subcategoryIdByName,
    onIntersection,
}: UseMaterialScrollSentinelsArgs): MaterialScrollSentinels => {
    // Ref for the global IntersectionObserver (search and flat mode).
    const globalSentinelObserver = useRef<IntersectionObserver | null>(null);
    // Map of per-group IntersectionObservers for browse mode pagination sentinels.
    const groupObservers = useRef<Map<string, IntersectionObserver>>(new Map());

    /**
     * Creates an IntersectionObserver ref callback for the last material option in a group.
     * When the sentinel is intersected, calls onIntersection(subcategoryId) to trigger
     * per-group pagination. Only used in browse mode (isGroupedByMaterialSubcategory && !isSearchActive).
     */
    const createGroupSentinelRef = useCallback(
        (groupName: string): SentinelRef =>
            (node) => {
                const subcategoryId = subcategoryIdByName?.[groupName];
                if (!subcategoryId) return;

                const existingObserver =
                    groupObservers.current.get(subcategoryId);
                if (existingObserver) existingObserver.disconnect();

                if (!node) return;

                /**
                 * IntersectionObserver reports a target's current visibility as soon as
                 * it's observed, even with no real scrolling. Since the sentinel moves to
                 * a new DOM node every time a fresh page appends (the previous "last
                 * option" is no longer last), that synthetic initial report would
                 * otherwise be mistaken for a real scroll-triggered intersection —
                 * fetching page after page automatically. Skip only that first report.
                 */
                let hasSkippedInitialReport = false;
                const groupSentinelObserver = new IntersectionObserver(
                    (entries) => {
                        if (!hasSkippedInitialReport) {
                            hasSkippedInitialReport = true;
                            return;
                        }
                        if (entries[0].isIntersecting) {
                            onIntersection?.(subcategoryId);
                        }
                    }
                );
                groupSentinelObserver.observe(node);
                groupObservers.current.set(
                    subcategoryId,
                    groupSentinelObserver
                );
            },
        [subcategoryIdByName, onIntersection]
    );

    // For search mode and flat mode, keep the single global sentinel on the last renderable option.
    const lastMaterialRef = useCallback<SentinelRef>(
        (node) => {
            if (globalSentinelObserver.current)
                globalSentinelObserver.current.disconnect();

            // See createGroupSentinelRef above: skip the synthetic initial report so
            // re-attaching to a new last option doesn't auto-trigger another fetch.
            let hasSkippedInitialReport = false;
            globalSentinelObserver.current = new IntersectionObserver(
                (entries) => {
                    if (!hasSkippedInitialReport) {
                        hasSkippedInitialReport = true;
                        return;
                    }
                    if (entries[0].isIntersecting) {
                        // In search mode there is no subcategoryId.
                        onIntersection?.(undefined);
                    }
                }
            );

            if (node) globalSentinelObserver.current.observe(node);
        },
        [onIntersection]
    );

    // Compute the last renderable option for each expanded group so we can place
    // the per-group scroll sentinel precisely on that option.
    const lastRenderableByGroupName = useMemo(() => {
        if (!isGroupedByMaterialSubcategory || isSearchActive) return null;
        const map = new Map<string, ValueType>();
        for (const option of materialOptions) {
            if (!isRenderableOption(option)) continue;
            const name = option.materialSubcategory.materialSubcategoryName;
            if (name && expandedGroups.has(name)) {
                // Keep updating — the last write for each name is the last renderable option.
                map.set(name, option);
            }
        }
        return map;
    }, [
        isGroupedByMaterialSubcategory,
        isSearchActive,
        materialOptions,
        expandedGroups,
    ]);

    const getSentinelRef = useCallback(
        ({
            option,
            options,
        }: {
            option: ValueType;
            options: Array<ValueType>;
        }): SentinelRef | null => {
            const isBrowseGrouped =
                isGroupedByMaterialSubcategory && !isSearchActive;

            // Browse mode: only the last renderable option of an expanded group gets a sentinel.
            if (isBrowseGrouped && lastRenderableByGroupName) {
                const groupName = getMaterialGroupName(option);
                if (
                    groupName &&
                    lastRenderableByGroupName.get(groupName) === option
                ) {
                    return createGroupSentinelRef(groupName);
                }
                return null;
            }

            // Search / flat mode: global sentinel on the very last renderable option.
            const lastRenderableOption = options.findLast(isRenderableOption);
            return option === lastRenderableOption ? lastMaterialRef : null;
        },
        [
            isGroupedByMaterialSubcategory,
            isSearchActive,
            lastRenderableByGroupName,
            createGroupSentinelRef,
            lastMaterialRef,
        ]
    );

    return { getSentinelRef };
};

export { useMaterialScrollSentinels };
export type { MaterialScrollSentinels };
